import { ReactEditor, useSlate } from 'slate-react';
import getSelectedNode, {
  GetSelectedNode,
} from 'components/text-editor/hooks/use-custom-editor/actions/get-selected-node';
import getFocusedNode, {
  GetFocusedNode,
} from 'components/text-editor/hooks/use-custom-editor/actions/get-focused-node';
import getSelectedTextSnippetNode, {
  GetSelectedTextSnippetNode,
} from 'components/text-editor/hooks/use-custom-editor/actions/get-selected-text-snippet-node';
import isTextSnippetSelected, {
  IsTextSnippetSelected,
} from 'components/text-editor/hooks/use-custom-editor/actions/is-text-snippet-selected';
import getSelectedText, {
  GetSelectedText,
} from 'components/text-editor/hooks/use-custom-editor/actions/get-selected-text';
import getTextFromNode, {
  GetTextFromNode,
} from 'components/text-editor/hooks/use-custom-editor/actions/get-text-from-node';
import createAnnotation, {
  CreateAnnotation,
} from 'components/text-editor/hooks/use-custom-editor/actions/create-annotation';
import updateAnnotation, {
  UpdateAnnotation,
} from 'components/text-editor/hooks/use-custom-editor/actions/update-annotation';
import saveAnnotation, {
  SaveAnnotation,
} from 'components/text-editor/hooks/use-custom-editor/actions/save-annotation';
import deleteAnnotation, {
  DeleteAnnotation,
} from 'components/text-editor/hooks/use-custom-editor/actions/delete-annotation';
import deleteCustomBlock, {
  DeleteCustomBlock,
} from 'components/text-editor/hooks/use-custom-editor/actions/delete-custom-block';
import markNodesAsSelected, {
  MarkNodesAsSelected,
} from 'components/text-editor/hooks/use-custom-editor/actions/mark-nodes-as-selected';
import { throttle } from 'lodash';
import getSlateRangeByAnnotationId, {
  GetSlateRangeByAnnotationId,
} from 'components/text-editor/hooks/use-custom-editor/actions/get-slate-range-by-annotation-id';
import saveNode, {
  SaveNode,
} from 'components/text-editor/hooks/use-custom-editor/actions/save-node';
import unMarkNodeAsSelected from 'components/text-editor/hooks/use-custom-editor/actions/un-mark-nodes-as-selected';
import getAnnotations, {
  GetAnnotations,
} from 'components/text-editor/hooks/use-custom-editor/actions/get-annotations';
import scrollNodeIntoView, {
  ScrollNodeIntoView,
} from 'components/text-editor/hooks/use-custom-editor/actions/scroll-node-into-view';
import filterNodes, {
  FilterNodes,
} from 'components/text-editor/hooks/use-custom-editor/actions/filter-nodes';
import findNode, {
  FindNode,
} from 'components/text-editor/hooks/use-custom-editor/actions/find-node';

export interface CustomEditor extends ReactEditor {
  createAnnotation: CreateAnnotation;
  updateAnnotation: UpdateAnnotation;
  saveNode: SaveNode;
  deleteAnnotation: DeleteAnnotation;
  deleteCustomBlock: DeleteCustomBlock;
  saveAnnotation: SaveAnnotation;
  getSelectedText: GetSelectedText;
  getSelectedNode: GetSelectedNode;
  getTextFromNode: GetTextFromNode;
  getFocusedNode: GetFocusedNode | any;
  getSelectedTextSnippetNode: GetSelectedTextSnippetNode;
  isTextSnippetSelected: IsTextSnippetSelected;
  markNodesAsSelected: MarkNodesAsSelected;
  unMarkNodesAsSelected: Function;
  getSlateRangeByAnnotationId: GetSlateRangeByAnnotationId;
  getAnnotations: GetAnnotations;
  scrollNodeIntoView: ScrollNodeIntoView;
  filterNodes: FilterNodes;
  findNode: FindNode;
}

/**
 * The withCustom hook wraps additional functions to the default ReactEditor
 * to make it easier to create, update and delete annotations.
 */
function withCustom(editor: CustomEditor): CustomEditor {
  editor.createAnnotation = createAnnotation(editor);
  editor.updateAnnotation = updateAnnotation(editor);
  // @ts-ignore
  editor.saveAnnotation = saveAnnotation(editor) as any;
  // @ts-ignore
  editor.saveNode = saveNode(editor);
  editor.deleteAnnotation = deleteAnnotation(editor);
  editor.deleteCustomBlock = deleteCustomBlock(editor);
  editor.getSelectedNode = getSelectedNode(editor);
  editor.getSelectedText = getSelectedText(editor);
  editor.getFocusedNode = throttle(getFocusedNode(editor), 500);
  editor.getSelectedTextSnippetNode = getSelectedTextSnippetNode(editor);
  editor.isTextSnippetSelected = isTextSnippetSelected(editor);
  editor.getTextFromNode = getTextFromNode;
  editor.markNodesAsSelected = markNodesAsSelected(editor);
  editor.unMarkNodesAsSelected = unMarkNodeAsSelected(editor);
  editor.getSlateRangeByAnnotationId = getSlateRangeByAnnotationId(editor);
  editor.getAnnotations = getAnnotations(editor);
  editor.scrollNodeIntoView = scrollNodeIntoView(editor);
  editor.filterNodes = filterNodes(editor);
  editor.findNode = findNode(editor);

  return editor as CustomEditor;
}

export default function useCustomEditor(): CustomEditor {
  return withCustom(useSlate() as CustomEditor);
}

export function useCustomEditorFromEditor(editor): CustomEditor {
  return withCustom(editor as CustomEditor);
}
